'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { LogisticsJob } from './types';

// ─── Priority options ─────────────────────────────────────────────────────────

const PRIORITY_OPTIONS = [
  { key: 'low',      label: 'Low',      activeBg: 'bg-gray-200',   activeText: 'text-gray-800',  activeRing: 'ring-gray-400' },
  { key: 'medium',   label: 'Medium',   activeBg: 'bg-amber-100',  activeText: 'text-amber-800', activeRing: 'ring-amber-400' },
  { key: 'high',     label: 'High',     activeBg: 'bg-orange-100', activeText: 'text-orange-800',activeRing: 'ring-orange-400' },
  { key: 'critical', label: 'Critical', activeBg: 'bg-red-100',    activeText: 'text-red-800',   activeRing: 'ring-red-500' },
] as const;

// ─── Props ────────────────────────────────────────────────────────────────────

interface LogIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobs: LogisticsJob[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const LogIncidentModal = ({ isOpen, onClose, jobs }: LogIncidentModalProps) => {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [affectedJobId, setAffectedJobId] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();
  const { currentUser } = useAuth();

  const isValid = title.trim().length >= 3 && description.trim().length >= 10 && !submitting;

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setTitle('');
        setPriority('medium');
        setAffectedJobId('');
        setDescription('');
        setSubmitting(false);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, submitting, onClose]);

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);

    try {
      const affectedJob = affectedJobId
        ? jobs.find(j => j.job_id === affectedJobId)
        : null;

      const ticketId = `ESC-${Date.now().toString(36).toUpperCase()}`;

      const now = new Date().toISOString();
      const reporterName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
      const reporterId = currentUser?.id ?? '';

      const timelineEntry = {
        action: 'incident_logged',
        actor: reporterName,
        actor_id: reporterId,
        timestamp: now,
        note: `Incident logged from Logistics dashboard`,
      };

      const insertPayload = {
        ticket_id: ticketId,
        title: title.trim(),
        description: description.trim(),
        source_department: 'Logistics',
        priority: priority,
        status: 'open',
        reported_by_id: reporterId,
        reported_by_name: reporterName,
        affected_job_id: affectedJobId || null,
        affected_job_address: affectedJob?.address ?? null,
        contact_directory: affectedJob?.contact_directory ?? {},
        assignment_history: [],
        timeline_log: [timelineEntry],
      };

      const { error } = await supabase
        .from('escalations')
        .insert(insertPayload);

      if (error) throw error;

      toast.success(`Incident ${ticketId} logged`, {
        duration: 4000,
        icon: '⚠️',
      });

      onClose();
    } catch (err) {
      console.error('Failed to log incident:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to log incident: ${message}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="log-incident-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 9v4M12 17h.01" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h3 id="log-incident-title" className="text-lg font-bold text-[#1C2E5E]">Log Incident</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">Report an issue to Operations</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Form body */}
        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label htmlFor="incident-title" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              id="incident-title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Material delivery blocked by construction"
              maxLength={120}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              autoFocus
            />
            <p className="text-[11px] text-gray-400 mt-1">{title.length}/120</p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Priority <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PRIORITY_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPriority(opt.key)}
                  className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-all ${
                    priority === opt.key
                      ? `${opt.activeBg} ${opt.activeText} ring-2 ${opt.activeRing}`
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Affected Job */}
          <div>
            <label htmlFor="incident-job" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Affected Job
              <span className="font-normal text-gray-400 ml-1">(optional)</span>
            </label>
            <div className="relative">
              <select
                id="incident-job"
                value={affectedJobId}
                onChange={e => setAffectedJobId(e.target.value)}
                className="w-full pl-3 pr-8 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all appearance-none cursor-pointer"
              >
                <option value="">— No specific job —</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.job_id}>
                    {j.job_id} — {j.address}
                  </option>
                ))}
              </select>
              <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="incident-description" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="incident-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What happened? Include any relevant details for the Ops team to triage."
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">{description.length}/1000</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            )}
            {submitting ? 'Logging…' : 'Log Incident'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogIncidentModal;
